import { PayloadAction } from '@reduxjs/toolkit';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, Observer } from 'rxjs';
import { debounceTime, delayWhen, filter, mergeMap, take } from 'rxjs/operators';

import config from '../../../config';
import { OrderStatusEnum } from '../../enums';
import { InboxPageRequest } from '../../models/ams/types';
import { DynamicFilterBuilder } from '../../services/filter/dynamic-filter-builder';
import GlobalService from '../../services/Global.service';
import {
  isAccountCreatedEvent,
  isAccountEvent,
  isAccountStatusChangedEvent,
  isCashAccountEvent,
  UserSSEEvent,
} from '../../services/user-events/sse-event-types';
import {
  clearDemoAccountActivating,
  hasBecomeFinished,
  hasBecomeUsable,
  isDemoAccountReference,
} from '../../util/account-helpers';
import { getStringLiteral } from '../../util/DataHelpers';
import {
  getAuthUserTradingAcount,
  IndividualExtendedInfoPayload,
  refreshPayments,
  servicesReady,
} from '../common-actions';
import { clearPlanPurchasePaymentCallback } from '../../util/url-helpers';
import { AccountEndReason, accountFinished } from '../account-flow';
import { areTradingPlansReady, getCreatedAccountStatus } from '../account-flow/selectors';
import { accountCreatedEventReceived, setUseDemoAccount } from '../crm';
import { toDetailedAccountFromEvent } from '../crm/stripping';
import { processResponse } from '../helpers';
import { PaymentCall } from '../payment/types';
import { RootState } from '..';

import { DEFAULT_INBOX_PAGE_SIZE, NO_MORE_PAGES_TO_READ } from './constants';
import {
  deleteMessages, deleteMessagesFail, deleteMessagesSuccess,
  getAllMessages, getAllMessagesFail, getAllMessagesSuccess,
  getMessage, getMessageFail, getMessageSuccess,
  getSummary, getSummaryFail, getSummarySuccess,
  markAllMessagesAsRead, markAllMessagesAsReadFail, markAllMessagesAsReadSuccess,
  markAsRead,
  markAsReadByMetadata,
  markAsReadByMetadataFail,
  markAsReadByMetadataSuccess,
  markAsReadFail,
  markAsReadSuccess,
  notifyUnreadCount, notifyUnreadCountFail, notifyUnreadCountSuccess,
  userSSEEventReceived,
} from './index';

type TypeGetAllMessagesEpic = PayloadAction<InboxPageRequest | void>

const getAllMessagesEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(servicesReady.type, getAllMessages.type),
  filter(() => state$.value.ams.nextPageToRead > NO_MORE_PAGES_TO_READ),
  mergeMap((action: TypeGetAllMessagesEpic) => new Observable((observer: Observer<any>) => {
    const callName = 'ams/getAllMessages';
    let params: InboxPageRequest = {
      size: DEFAULT_INBOX_PAGE_SIZE,
      page: state$.value.ams.nextPageToRead,
      sort: 'receptionTime,desc', // Get all messages in descending order
      filters: new DynamicFilterBuilder().equal('customerProductId', config.portalId).build(),
    };
    if (
      typeof action.payload === 'object'
      && 'size' in action.payload
    ) {
      params = {
        ...params,
        size: action.payload.size,
      };
    }
    GlobalService
      ?.amsService
      ?.getAllMessages(params)
      .then(response => {
        processResponse(callName, {}, response, observer, getAllMessagesSuccess, getAllMessagesFail);
      });
  })),
);

const deleteMessageEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(deleteMessages.type),
  mergeMap((action: PayloadAction<number | number[]>) => new Observable(
    (observer: Observer<any>) => {
      const callName = 'ams/deleteMessages';

      GlobalService
        ?.amsService
        ?.deleteMessages(action.payload)
        .then(response => {
          processResponse(
            callName,
            {},
            response,
            observer,
            deleteMessagesSuccess,
            deleteMessagesFail,
            false,
            { ids: getStringLiteral(action.payload) },
          );
        });
    },
  )),
);

const getMessageEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getMessage.type),
  mergeMap((action: PayloadAction<number>) => new Observable((observer: Observer<any>) => {
    const callName = 'ams/getMessage';

    GlobalService
      ?.amsService
      ?.getMessage(action.payload)
      .then(response => {
        processResponse(callName, {}, response, observer, getMessageSuccess, getMessageFail);
      });
  })),
);

const markAllMessagesAsReadEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(markAllMessagesAsRead.type),
  mergeMap((action: PayloadAction) => new Observable((observer: Observer<PayloadAction>) => {
    const callName = 'ams/markAllMessagesAsRead';

    GlobalService
      ?.amsService
      ?.markAllMessagesAsRead()
      .then(response => {
        processResponse(callName, {}, response, observer, markAllMessagesAsReadSuccess, markAllMessagesAsReadFail);
      });
  })),
);

const markAsReadEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(markAsRead.type),
  mergeMap((action: PayloadAction<number | number[]>) => new Observable((observer: Observer<any>) => {
    const callName = 'ams/markAsRead';

    GlobalService
      ?.amsService
      ?.markAsRead(action.payload)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          markAsReadSuccess,
          markAsReadFail,
          false,
          { ids: getStringLiteral(action.payload) },
        );
      });
  })),
);

const updateHasUnreadMessagesEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(
    getSummary.type,
    getAllMessagesSuccess.type,
    markAsReadSuccess.type,
    markAllMessagesAsReadSuccess.type,
    deleteMessagesSuccess.type,
  ),
  mergeMap((action: PayloadAction) => new Observable((observer: Observer<any>) => {
    const callName = 'ams/getSummary';

    GlobalService
      ?.amsService
      ?.getSummary()
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getSummarySuccess,
          getSummaryFail,
          false,
          { ids: action.payload },
        );
      });
  })),
);

const notifyUnreadCountEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(notifyUnreadCount.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'ams/notifyUnreadCount';
    const requestBody = {
      notifyInterval: 1,
      includedSubjects: [
        OrderStatusEnum.Cancelled,
        OrderStatusEnum.Expired,
        OrderStatusEnum.Filled,
        OrderStatusEnum.PartiallyReject,
        OrderStatusEnum.Rejected,
      ],
      groupedBySubjects: true,
      importanceLevel: 10,
    };

    GlobalService.amsService.notifyUnreadCount(requestBody)
      .then(response => {
        processResponse(callName, {}, response, observer, notifyUnreadCountSuccess, notifyUnreadCountFail);
      });
  })),
);

const markAsReadByMetadataEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(markAsReadByMetadata.type),
  debounceTime(1000),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'ams/notifyUnreadCount';
    const requestBody = {
      key: 'OrderID',
      values: [ state$.value.trading.order?.clientOrderId ?? '' ],
    };

    GlobalService.amsService.markAsReadByMetadata(requestBody)
      .then(response => {
        processResponse(callName, {}, response, observer, markAsReadByMetadataSuccess, markAsReadByMetadataFail);
      });
  })),
);

const userAccountEventsEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(userSSEEventReceived.type),
  filter(({ payload }: PayloadAction<UserSSEEvent>) => isAccountEvent(payload) && !isCashAccountEvent(payload)),
  delayWhen(() => state$.pipe(filter(() => areTradingPlansReady(state$.value)), take(1))),
  mergeMap(({ payload }: PayloadAction<UserSSEEvent>) => new Observable((observer: Observer<any>) => {
    if (isAccountCreatedEvent(payload)) {
      const accountStatus = getCreatedAccountStatus(state$.value, payload.data.tradingPlanId);

      observer.next(accountCreatedEventReceived(toDetailedAccountFromEvent(payload, accountStatus)));

      if (!isDemoAccountReference(payload.data.accountReferenceId)) {
        observer.next(setUseDemoAccount(false));
      }

      observer.complete();
      return;
    }

    if (isAccountStatusChangedEvent(payload)) {
      const { accountReferenceId, previousStatus, status } = payload.data;

      if (hasBecomeUsable(previousStatus, status)) {
        const extendedInfo = state$.value.crm.individualExtendedInfo as IndividualExtendedInfoPayload;

        if (isDemoAccountReference(accountReferenceId)) {
          clearDemoAccountActivating();
        } else {
          // The purchased account is usable, so the plan callback is over and the app leaves demo.
          clearPlanPurchasePaymentCallback();
          observer.next(setUseDemoAccount(false));
        }

        observer.next(getAuthUserTradingAcount(extendedInfo));
        observer.next(refreshPayments({ [PaymentCall.getPaymentsAll]: true }));
      }

      if (hasBecomeFinished(previousStatus, status)) {
        const extendedInfo = state$.value.crm.individualExtendedInfo as IndividualExtendedInfoPayload;

        observer.next(accountFinished({ accountReferenceId, reason: AccountEndReason.Unknown }));
        observer.next(getAuthUserTradingAcount(extendedInfo));
      }
    }

    observer.complete();
  })),
);

export default combineEpics(
  getAllMessagesEpic,
  deleteMessageEpic,
  getMessageEpic,
  markAllMessagesAsReadEpic,
  markAsReadEpic,
  markAsReadByMetadataEpic,
  notifyUnreadCountEpic,
  updateHasUnreadMessagesEpic,
  userAccountEventsEpic,
);
